/** @jsxImportSource @emotion/react */
import React from "react";
import moment from "moment";
import Immutable from "immutable";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import TextField from "@mui/material/TextField";

import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as KpiCalculator from "js/common/kpi-calculator";
import * as DashboardUtils from "js/dashboards/utils";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import * as kpiRepo from "js/common/repo/backbone/kpi-repo";
import * as Formatter from "js/common/utils/formatter";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";

import PriorityMetric from "js/common/views/priority-metric";
import KpiPicker from "js/common/views/kpis/kpi-picker";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";

import {formatChangePct} from "js/dashboards/components/change-pct";
import DataConfigToolbar from "js/dashboards/data-config-toolbar";
import * as Overlays from "js/dashboards/components/overlays";
import NoTargetPriorityMetric from "js/common/views/no-target-priority-metric";
import KpiDetailsDialog from "js/oneview/kpi-details/dialog";
import {renderUserOrGroupDetails} from "js/dashboards/utils";
import {createCommonConfig} from "js/dashboards/ai-assistant/utils";
import {computePriorTimeframe, isPriorPeriodComputable} from "js/dashboards/components/prior-period";

// Date-range fallback for the prior period sub-label (e.g. "20 Dec 2024–31 Dec 2024").
// Both endpoints are always stated in full: abbreviating a shared month or year
// makes a partial window read as the whole calendar month/year it sits inside,
// overstating the period the figure was measured over.
export const formatPriorPeriodLabel = (start, end) => {
  if (!start || !end) return "Prior";
  const sameDay = start.year() === end.year() && start.month() === end.month() && start.date() === end.date();
  if (sameDay) return start.format("D MMM YYYY");
  return `${start.format("D MMM YYYY")}–${end.format("D MMM YYYY")}`;
};

// Human-readable "vs. X" label for the prior period, derived from the timeframe's
// id and display name. Falls back to a formatted date range when the timeframe
// doesn't match a known pattern.
export const formatPriorPeriodVsLabel = (timeframeId, timeframeName, priorStart, priorEnd) => {
  const id = (timeframeId || "").toLowerCase();

  // ID-based rules first — covers WTD/MTD/QTD/YTD regardless of display name.
  if (id === "today") return "vs. Yesterday";
  if (id === "wtd")   return "vs. Previous Week";
  if (id === "mtd")   return "vs. Last Month";
  if (id === "qtd")   return "vs. Last Quarter";
  if (id === "ytd")   return "vs. Last Year";

  // Name-based rules — only for simple names to avoid mangling compound ones
  // like "This Month Last Year" → "Last Month Last Year" (nonsensical).
  const name = (timeframeName || "").trim();
  const afterThis = name.startsWith("This ") ? name.slice(5) : null;
  const afterLast = name.startsWith("Last ") ? name.slice(5) : null;
  // "This Month/Week/Quarter/Year" (single word after "This ")
  if (afterThis && !afterThis.includes(" ")) return `vs. Last ${afterThis}`;
  // "Last Week/Month/Quarter/Year" or "Last N Days/Weeks/Months" (single word, or digits + word)
  if (afterLast && (!afterLast.includes(" ") || /^\d+ \w+$/.test(afterLast))) return `vs. Previous ${afterLast}`;

  // Fallback: computed date range.
  return `vs. ${formatPriorPeriodLabel(priorStart, priorEnd)}`;
};

export {formatChangePct} from "js/dashboards/components/change-pct";

// numbers.formatBigNumber rounds within a unit instead of promoting to the next one, so
// 999,999 comes back as "1000K". Reading "1000K" next to a pill capped at "1M" is the
// same inconsistency AC2 removes from the percentage, so it is corrected here too.
export const promoteAbbreviation = formatted =>
  typeof formatted === "string"
    ? formatted.replace(/1000K/, "1M").replace(/1000M/, "1B")
    : formatted;

const PILL_VARIANTS = {
  positive: {bg: "#E9F5E1", iconColor: "#107C10", textColor: "#39531E"},
  negative: {bg: "#F7D9DC", iconColor: "#DA4453", textColor: "#5E131A"},
  neutral:  {bg: "#F2F2F2", iconColor: "#757575", textColor: "#424242"},
};

const ComparisonPill = React.memo(({changePct, higherIsBetter}) => {
  if (changePct === null) return null;

  const isNeutral = changePct === 0;
  const isPositive = changePct > 0;
  const isGood = !isNeutral && (isPositive === higherIsBetter);
  const variantKey = isNeutral ? "neutral" : (isGood ? "positive" : "negative");
  const {bg, iconColor, textColor} = PILL_VARIANTS[variantKey];
  const iconClass = isNeutral ? null : (isPositive ? "bhi-trending-up" : "bhi-trending-down");
  const sign = isPositive ? "+" : (changePct < 0 ? "-" : "");
  const formatted = formatChangePct(changePct);

  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 5,
      background: bg, color: textColor,
      // The pill is the shortest thing in the row, so it is the metric value that gives
      // way when space runs out — never the pill spilling past the panel edge.
      flexShrink: 0, maxWidth: "100%", whiteSpace: "nowrap"
    }}>
      {iconClass
        ? <i className={iconClass} style={{fontSize: 11, color: iconColor}} />
        : <span style={{fontSize: 11, color: iconColor, lineHeight: 1}}>—</span>
      }
      {sign}{formatted}
    </span>
  );
});

const MetricWithComparison = React.memo(({config, data, kpi, onClick}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const comparison = config.get("comparison");
  // total is the wrapper object {value: N} that Formatter.format expects
  const primaryTotal = data.getIn(["value", "primary", "total"]);
  const baselineTotal = data.getIn(["value", "baseline", "total"]);
  const primaryValue = primaryTotal?.get("value") ?? null;
  const baselineValue = baselineTotal?.get("value") ?? null;
  const higherIsBetter = comparison.get("higherIsBetter", true);

  const changePct = (primaryValue !== null && primaryValue !== 0 && baselineValue !== null && baselineValue !== 0)
    ? ((primaryValue - baselineValue) / baselineValue) * 100
    : null;

  const isPriorPeriod = comparison.get("type") === "prior_period";
  // For prior-period the baseline is the same KPI over the previous timeframe,
  // so format it with the primary KPI and label it "vs. Previous <timeframe name>".
  const baselineKpi = isPriorPeriod ? kpi : KpiRepo.get(comparison.get("baselineKpiId"));
  const subLabel = (() => {
    if (!isPriorPeriod) return comparison.get("baselineLabel") || (baselineKpi ? baselineKpi.get("name") : "");
    const timeframeConfig = config.get("timeframe");
    const resolvedTimeframe = timeframeConfig ? TimeframeRepo.parse(timeframeConfig.toJS()) : null;
    const priorTf = resolvedTimeframe ? computePriorTimeframe(resolvedTimeframe) : null;
    return formatPriorPeriodVsLabel(
      resolvedTimeframe?.get("id"),
      resolvedTimeframe?.get("name"),
      priorTf?.start,
      priorTf?.end
    );
  })();

  const formattedPrimary = kpi && primaryTotal
    ? promoteAbbreviation(Formatter.format(primaryTotal, {maxDisplayLength: 6, valueFormat: kpi.get("valueFormat")}))
    : (primaryValue ?? "—");
  // The sub-label shares its line with a metric name that can run to any length, so its
  // figure abbreviates from a thousand up — the same threshold the pill uses — rather
  // than at the six characters the headline figure can afford on a line of its own.
  const formattedBaseline = baselineKpi && baselineTotal
    ? promoteAbbreviation(Formatter.format(baselineTotal, {maxDisplayLength: 3, valueFormat: baselineKpi.get("valueFormat")}))
    : (baselineValue ?? "—");

  const isNeutral = changePct === 0;
  const isGoodColor = !isNeutral && changePct !== null && (changePct > 0) === higherIsBetter;
  const primaryColor = changePct === null || isNeutral
    ? theme.palette.text.main
    : (isGoodColor ? theme.palette.dashboards.ahead : theme.palette.dashboards.behind);

  return (
    <div
      onClick={onClick}
      style={{cursor: "pointer", padding: "8px 12px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden"}}>
      <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, minWidth: 0}}>
        <span style={{
          fontSize: 36, fontWeight: 700, lineHeight: 1, color: primaryColor,
          minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
        }}>
          {formattedPrimary ?? "—"}
        </span>
        <ComparisonPill changePct={changePct} higherIsBetter={higherIsBetter} />
      </div>
      {/* The name is the part that truncates: the comparison figure is the point of the
          line, so it keeps its width and the label ellipsises around it. */}
      <div style={{display: "flex", gap: 4, fontSize: 12, color: theme.palette.text.secondary, marginTop: 4, minWidth: 0}}>
        <span title={subLabel} style={{overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", minWidth: 0}}>
          {subLabel}:
        </span>
        <span style={{flexShrink: 0}}>{formattedBaseline ?? "—"}</span>
      </div>
    </div>
  );
});

const MetricCurve = React.memo(({
  dimensions,
  data,
  config,
  onRequestDialog,
  onParentConfigChange
}) => {
  const kpi = KpiRepo.get(config.get("kpiId"));
  const notFilterableByClient = kpi && (!config.getIn(["clientFilter", "allClientIds"]).isEmpty() && !kpi.get(
      "filterableByClient"));
  const hasTarget = data.getIn(["value", "target", "value"], 0) > 0;
  const comparison = config.get("comparison");
  const hasComparison = comparison
      && (comparison.get("type") === "metric" || comparison.get("type") === "prior_period");

  if (!kpi) {
    return <Overlays.Message coverOtherContent={false}>
      Choose a Metric
    </Overlays.Message>;
  } else if (hasComparison) {
    return <MetricWithComparison
        config={config}
        data={data}
        kpi={kpi}
        onClick={() => renderDialog(config, onParentConfigChange, onRequestDialog)}
    />;
  } else if (hasTarget && !notFilterableByClient) {
    return <PriorityMetric
        kpiId={config.get("kpiId")}
        dimensions={dimensions}
        kpiData={data
            .get("value")
            .update("total", x => x.toJS())
            .update("expectedValue", x => x.toJS())
            .update("target", x => x.toJS())}
        onClick={() => renderDialog(config, onParentConfigChange, onRequestDialog)}
        clientIds={config.getIn(["clientFilter", "allClientIds"], Immutable.Set())}
        hasAppliedTagFilters={hasAppliedTagFilters(config)} />;
  } else {
    return <NoTargetPriorityMetric
        dimensions={dimensions}
        config={config}
        data={data}
        onClick={() => renderDialog(config, onParentConfigChange, onRequestDialog)}
        clientIds={config.getIn(["clientFilter", "allClientIds"], Immutable.Set())}
    />;
  }
});

const MetricCurveEditor = React.memo(({
  component,
  onComponentChange
}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const config = component.get("dataConfig");
  const comparison = config.get("comparison");
  const hasMetricComparison = comparison && comparison.get("type") === "metric";
  const hasPriorComparison = comparison && comparison.get("type") === "prior_period";
  const kpiSelected = !!config.get("kpiId");
  const timeframe = config.get("timeframe") ? TimeframeRepo.parse(config.get("timeframe").toJS()) : null;
  const priorComputable = isPriorPeriodComputable(timeframe);
  const comparisonMode = hasMetricComparison ? "metric" : hasPriorComparison ? "prior_period" : "none";

  const onConfigChange = React.useCallback(newDataConfig => {
    onComponentChange(component.set("dataConfig", newDataConfig));
  }, [onComponentChange, component]);

  const kpis = React.useMemo(
      () => Immutable.fromJS(KpiRepo
          .getAll()
          .toJSON())
          .filter(k => k.get("visible") || k.get("id") === config.get("kpiId")),
      [config]);

  const baselineKpis = React.useMemo(
      () => Immutable.fromJS(KpiRepo
          .getAll()
          .toJSON())
          .filter(k => k.get("visible") || k.get("id") === comparison?.get("baselineKpiId")),
      [comparison]);

  const onComparisonChange = patch => {
    const current = comparison || Immutable.Map({type: "metric", higherIsBetter: true});
    onConfigChange(config.set("comparison", current.merge(Immutable.fromJS(patch))));
  };

  return <div style={{padding: "0.5rem"}}>
    <DataConfigToolbar config={config} onConfigChange={onConfigChange} />

    <div style={{width: "50%", marginBottom: "0.3rem", marginTop: "0.5rem"}}>
      <KpiPicker
          placeholder="Choose Metric"
          multiSelect={false}
          clearable={false}
          kpis={kpis}
          selectedKpiId={config.get("kpiId")}
          onChange={kpiId => {
            const kpi = kpiRepo.get(kpiId);
            onConfigChange(config.set("kpiId", kpiId).set("title", kpi.get("name")));
          }} />
    </div>

    {kpiSelected && <>
      <RadioGroup
          value={comparisonMode}
          onChange={(e, value) => {
            if (value === "none") {
              onConfigChange(config.delete("comparison"));
            } else if (value === "metric") {
              onConfigChange(config.set("comparison", Immutable.fromJS({type: "metric", baselineKpiId: null, baselineLabel: "", higherIsBetter: true})));
            } else if (value === "prior_period") {
              onConfigChange(config.set("comparison", Immutable.fromJS({type: "prior_period", higherIsBetter: true})));
            }
          }}
          style={{marginTop: "0.5rem"}}>
        <FormControlLabel value="none" control={<Radio size="small" />} label="No comparison" />
        <FormControlLabel value="metric" control={<Radio size="small" />} label="Compare against another metric" data-testid="metric-compare-another-metric" />
        <Tooltip
            title={!priorComputable ? "Prior period comparison isn't available for this timeframe." : ""}
            placement="right">
          {/* A disabled control emits no pointer events, so the tooltip needs a live
              wrapper of its own to hover; flex-start stops it stretching across the row. */}
          <span
              style={{display: "inline-block", alignSelf: "flex-start"}}
              data-testid="metric-compare-prior-period-tooltip">
            <FormControlLabel
                value="prior_period"
                control={<Radio size="small" />}
                label="Compare against prior period"
                disabled={!priorComputable}
                data-testid="metric-compare-prior-period" />
          </span>
        </Tooltip>
      </RadioGroup>

      {hasMetricComparison && <div style={{marginTop: "0.5rem", display: "flex", flexDirection: "column", gap: 8}}>
        <div style={{width: "50%"}}>
          <KpiPicker
              placeholder="Comparison Metric"
              multiSelect={false}
              clearable={false}
              kpis={baselineKpis}
              selectedKpiId={comparison.get("baselineKpiId")}
              onChange={kpiId => onComparisonChange({baselineKpiId: kpiId})} />
        </div>
        <TextField
            size="small"
            label="Label (optional)"
            placeholder="e.g. Non-AI"
            value={comparison.get("baselineLabel", "")}
            onChange={e => onComparisonChange({baselineLabel: e.target.value})}
            style={{width: "50%"}} />
        <div>
          <div style={{fontSize: 11, color: theme.palette.text.secondary, marginBottom: 4}}>Higher number is</div>
          <ToggleButtonGroup
              size="small"
              exclusive
              value={comparison.get("higherIsBetter", true)}
              onChange={(e, val) => { if (val !== null) onComparisonChange({higherIsBetter: val}); }}>
            <ToggleButton value={true}>Good</ToggleButton>
            <ToggleButton value={false}>Bad</ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>}

      {hasPriorComparison && <div style={{marginTop: "0.5rem"}}>
        <div style={{fontSize: 11, color: theme.palette.text.secondary, marginBottom: 4}}>Higher number is</div>
        <ToggleButtonGroup
            size="small"
            exclusive
            value={comparison.get("higherIsBetter", true)}
            onChange={(e, val) => { if (val !== null) onComparisonChange({higherIsBetter: val}); }}>
          <ToggleButton value={true}>Good</ToggleButton>
          <ToggleButton value={false}>Bad</ToggleButton>
        </ToggleButtonGroup>
      </div>}
    </>}
  </div>;
});

const renderDialog = (config, onParentConfigChange, onRequestDialog) => {
  const kpi = kpiRepo.get(config.get("kpiId"));
  // TODO JC - why are the tag id params not set here?
  // TODO JC - is it expected for client / tag id params to be immutable when passed to the dialog?
  const kpiDetailsOptions = {
    kpiId: config.get("kpiId"),
    timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
    clientIds: config.getIn(["clientFilter", "allClientIds"], Immutable.List()),
    entityToIds: (config.get("entityToIds") ?? Immutable.Map()).toObject(),
    ...DashboardUtils.configToQualifierAjaxParams(config)
  };
  const title = (
      <div style={{display: "inline-block"}}>
      <span
          style={{
            fontSize: 20,
            textTransform: "none",
            fontWeight: 500
          }}>
          {kpi.get("name")}
      </span>
        <span style={{fontWeight: 500}}>
        {` (${TimeframeRepo.parse(config.get("timeframe").toJS()).attributes.name})`}
      </span>
        {renderUserOrGroupDetails(config)}
      </div>
  );
  const kpiDialogRef = React.createRef();
  const handleKpiDialogRequestClose = () => closeDialog();
  const handleDialogRequestClose = () => kpiDialogRef.current.handleCloseRequest();
  const closeDialog = onRequestDialog(
      <KpiDetailsDialog
          ref={kpiDialogRef}
          renderContentOnly
          {...kpiDetailsOptions}
          matchAnyTagIds={config.getIn(["tagFilter", "matchAnyTagIds"], Immutable.List())}
          matchAllTagIds={config.getIn(["tagFilter", "matchAllTagIds"], Immutable.List())}
          excludedTagIds={config.getIn(["tagFilter", "excludedTagIds"], Immutable.List())}
          onRequestClose={handleKpiDialogRequestClose}
          onGroupClick={groupId => {
            onParentConfigChange(parentConfig => parentConfig.set("qualifier", Immutable.Map({
              type: "GROUP",
              id: groupId
            })), true);
            closeDialog();
          }}
          onUserClick={userId => {
            onParentConfigChange(parentConfig => parentConfig.set("qualifier", Immutable.Map({
              type: "USER",
              id: userId
            })), true);
            closeDialog();
          }} />,
      {title: title, onRequestClose: handleDialogRequestClose});
};

const hasAppliedTagFilters = (config) => {
  return !config.getIn(["tagFilter", "matchAnyTagIds"]).isEmpty() ||
      !config.getIn(["tagFilter", "matchAllTagIds"]).isEmpty() ||
      !config.getIn(["tagFilter", "excludedTagIds"]).isEmpty();
};

const transformMetricCurveConfig = (backendConfig, dashboardConfig) => {
  const kpiId = backendConfig.get("kpiIds")?.get(0);
  const commonConfig = createCommonConfig(backendConfig, dashboardConfig);

  return commonConfig.merge({
    kpiId: kpiId
  });
};

export default Immutable.fromJS({
  type: "MetricCurve",
  label: "Metric",
  titleIsEditable: true,

  previewDimensions: Immutable.Map({
    width: 200,
    height: 100
  }),
  expandedPreviewDimensions: Immutable.Map({
    width: 400,
    height: 130
  }),

  transformConfig: (backendConfig, dashboardConfig) =>
      transformMetricCurveConfig(backendConfig, dashboardConfig),

  transformData: data => Immutable.Map({value: data}),

  getTitle: component => {
    if (component) {
      const config = component.get("dataConfig");
      if (config) {
        const kpiId = config.get("kpiId");
        const kpi = kpiRepo.get(kpiId);
        if (kpi) {
          return component.get("title") || component.get("title") === "" ? component.get("title") : kpi.get("name");
        } else {
          return "New Metric";
        }
      } else {
        return "New Metric";
      }
    } else {
      return "New Metric";
    }
  },
  getDefaultData: () => Immutable.Map(),
  getReactComponent: () => MetricCurve,
  getEditorReactComponent: () => MetricCurveEditor,
  canFullScreen: false,
  variant: "standard",
  layout: {
    min: {width: 6, height: 4},
    max: {width: 16, height: 16},
    init: {width: 6, height: 4}
  },
  applyParentConfig: (parentConfig, dataConfig) => DashboardUtils.applyInheritanceForKeys(parentConfig, dataConfig),
  load: component => {
    const config = component.get("dataConfig");
    const kpiId = config.get("kpiId");
    if (!config.has("kpiId")) {
      return Promise.resolve(Immutable.Map());
    }
    const kpi = kpiRepo.get(kpiId);
    if (!kpi) {
      return Promise.resolve(Immutable.Map());
    }
    if (!config.getIn(["clientFilter", "allClientIds"]).isEmpty() && !kpi.get("filterableByClient")) {
      return Promise.resolve(Immutable.Map());
    }
    const ajaxParams = {
      dateFromUI: moment().format("YYYY-MM-DD"),
      timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
      clientIds: config.getIn(["clientFilter", "allClientIds"]).toArray(),
      matchAnyTagIds: config.getIn(["tagFilter", "matchAnyTagIds"]).toArray(),
      matchAllTagIds: config.getIn(["tagFilter", "matchAllTagIds"]).toArray(),
      excludedTagIds: config.getIn(["tagFilter", "excludedTagIds"]).toArray(),
      entityToIds: (config.get("entityToIds") ?? Immutable.Map()).toObject(),
      ...DashboardUtils.configToQualifierAjaxParams(config)
    };

    const comparison = config.get("comparison");
    const baselineKpiId = comparison?.get("baselineKpiId");
    if (comparison && comparison.get("type") === "metric" && baselineKpiId) {
      return Promise.all([
        KpiCalculator.summary(kpiId, ajaxParams),
        KpiCalculator.summary(baselineKpiId, ajaxParams)
      ]).then(([primary, baseline]) => Immutable.fromJS({primary, baseline}));
    }

    if (comparison && comparison.get("type") === "prior_period") {
      const priorTimeframe = computePriorTimeframe(ajaxParams.timeframe);
      if (priorTimeframe) {
        // Same KPI, prior timeframe — a second parallel call merged as the baseline.
        const priorParams = {...ajaxParams, timeframe: TimeframeRepo.parseCustom(priorTimeframe)};
        return Promise.all([
          KpiCalculator.summary(kpiId, ajaxParams),
          KpiCalculator.summary(kpiId, priorParams)
        ]).then(([primary, baseline]) => Immutable.fromJS({primary, baseline}));
      }
    }

    return KpiCalculator
        .summary(kpiId, ajaxParams)
        .then(response => Immutable.fromJS(response));
  }
});
